import { z } from "zod";
import { AiInsightDraft, ModelDerivedTimestamp } from "./insight";

// ---------------------------------------------------------------------------
// Call digest (member-performance insights)
//
// Stage 1 of the `ai-member-performance` generator: a call-source adapter
// (Composio/Garba today, custom MCP sources later) gathers one rep's recent
// recorded sales calls into a normalized digest. The digest is then fed,
// clearly delimited as untrusted data, into the structured synthesis stage
// together with the member's KPIs, objectives, and memory.
//
// Same posture as external-digest.ts: hard caps truncate instead of rejecting
// (pure model output — one long field must not lose a whole gather), and
// identity fields are always overwritten server-side.
// ---------------------------------------------------------------------------

export const CALL_DIGEST_MAX_ITEMS = 10;

/**
 * Oldest first, undated last. Stable, so equal/undated items keep the order
 * the model emitted them in.
 *
 * This is what makes the `CALL_DIGEST_MAX_ITEMS` cap safe (GRA-47). Neither
 * prompt fixes the order the model lists calls in, and the gather watermark
 * afterwards advances to the newest call in the digest - so capping in
 * model order can drop the *oldest* calls of an over-cap backlog and then
 * step the watermark past them, losing them for good. Capping the oldest-first
 * order instead drops the newest, which the next night's window still covers:
 * a backlog drains chronologically over successive runs, never silently.
 */
export function orderCallsChronologically<T extends { occurredAt: string | null }>(
  items: T[],
): T[] {
  const at = (item: T): number => {
    if (item.occurredAt == null) return Number.POSITIVE_INFINITY;
    const ms = Date.parse(item.occurredAt);
    // NaN would make the comparator inconsistent (every comparison false), so
    // an unparseable value is sorted with the undated ones instead.
    return Number.isFinite(ms) ? ms : Number.POSITIVE_INFINITY;
  };
  return [...items].sort((a, b) => {
    const [x, y] = [at(a), at(b)];
    // Not `x - y`: two undated items would give Infinity - Infinity = NaN,
    // which is an invalid comparator result.
    if (x === y) return 0;
    return x < y ? -1 : 1;
  });
}

export const CallDigestItem = z.object({
  /** The provider's own id for the call — keys per-call insight dedupe. */
  externalId: z.string().min(1),
  /** Uppercase Composio toolkit slug, e.g. "CUSTOM_GARBA_AI". */
  toolkit: z.string().min(1),
  title: z
    .string()
    .min(1)
    .describe("Kort titel, max 80 tecken")
    .transform((s) => s.slice(0, 80)),
  summary: z
    .string()
    .min(1)
    .describe(
      "Vad diskuterades och vad erbjöds/pitchades — konkret, max 400 tecken",
    )
    .transform((s) => s.slice(0, 400)),
  /** When the call happened (ISO), if the provider exposes it. */
  occurredAt: ModelDerivedTimestamp,
  /** Customer/company on the call. */
  counterpart: z.string().nullable().default(null),
  /** Deep link to the call in the provider's app. */
  url: z.string().nullable().default(null),
  evidence: z
    .array(z.string().transform((s) => s.slice(0, 200)))
    .describe("Max 3 korta citat/punkter ur samtalet")
    .default([])
    .transform((a) => a.slice(0, 3)),
});
export type CallDigestItem = z.infer<typeof CallDigestItem>;

export const CallUserDigest = z.object({
  uid: z.string().min(1),
  memberId: z.string().min(1),
  memberName: z.string().nullable().default(null),
  /** Which adapter produced this digest (cursor key). */
  adapterKey: z.string().min(1),
  /** Toolkits that actually contributed calls. */
  toolkits: z.array(z.string()).default([]),
  items: z
    .array(CallDigestItem)
    .describe("Max 10 samtal, äldsta först")
    .default([])
    .transform((a) => orderCallsChronologically(a).slice(0, CALL_DIGEST_MAX_ITEMS)),
  fetchedAt: z.string(),
});
export type CallUserDigest = z.infer<typeof CallUserDigest>;

// ---------------------------------------------------------------------------
// Synthesis drafts
// ---------------------------------------------------------------------------

export const MEMBER_CALL_INSIGHTS_MAX = 4;

/**
 * Synthesis-stage draft for one member-performance insight. Scope/member
 * targeting and visibility are never model-controlled — the generator forces
 * scope "member" + the gathered member's id server-side.
 */
export const MemberCallInsightDraft = AiInsightDraft.omit({
  scope: true,
  memberId: true,
  salesTeamId: true,
  // Call insights are grounded in gathered calls, never the vault.
  sourceDocumentTitles: true,
}).extend({
  /** externalIds of the digest calls the insight is built on. */
  externalCallIds: z
    .array(z.string())
    .describe("De samtals-id:n (externalId) insikten bygger på, max 3")
    .default([])
    .transform((a) => a.slice(0, 3)),
  /** Call time the insight is about — the timeline anchors on this. */
  occurredAt: ModelDerivedTimestamp,
  /**
   * Optional quantified KPI framing. The definitionId must reference a KPI the
   * generator listed in the prompt; the statement must only restate numbers
   * provided there — the generator validates the id and folds the statement
   * into `evidence`.
   */
  kpiImpact: z
    .object({
      definitionId: z.string().nullable().default(null),
      statement: z
        .string()
        .describe(
          "T.ex. '3 av 5 samtal utan merförsäljning — mål 8/vecka, nu 5'",
        )
        .transform((s) => s.slice(0, 160)),
    })
    .nullable()
    .default(null),
});
export type MemberCallInsightDraft = z.infer<typeof MemberCallInsightDraft>;

export const MemberCallInsightBatch = z.object({
  insights: z
    .array(MemberCallInsightDraft)
    .describe(`Max ${MEMBER_CALL_INSIGHTS_MAX} insikter`)
    .transform((a) => a.slice(0, MEMBER_CALL_INSIGHTS_MAX)),
});
export type MemberCallInsightBatch = z.infer<typeof MemberCallInsightBatch>;
